import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Nav from "./Navigation.js";
import { getTokenInternal } from "./useToken";

export default function ManageAnnouncements() {
  const [token, setToken] = useState();
  const [announcements, setAnnouncements] = useState([]);
  const [message, setMessage] = useState("");
  const [expiresInDays, setExpiresInDays] = useState(14);
  const [formError, setFormError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const init = async () => {
      const fetchedToken = await getTokenInternal();
      if (
        !fetchedToken ||
        !["owner", "management", "helper"].includes(fetchedToken.account.role)
      ) {
        navigate("/");
        return;
      }
      setToken(fetchedToken);
    };
    init();
  }, []);

  const loadAnnouncements = async (currentToken) => {
    const response = await fetch(
      `${process.env.REACT_APP_API_HOST}/api/announcements`,
      { headers: { Authorization: `Bearer ${currentToken.access_token}` } }
    );
    if (response.ok) {
      setAnnouncements(await response.json());
    }
  };

  useEffect(() => {
    if (token) loadAnnouncements(token);
  }, [token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/announcements/create`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            message,
            expires_in_days: Number(expiresInDays),
          }),
        }
      );
      if (!response.ok) {
        throw new Error("Could not create announcement");
      }
      setMessage("");
      setExpiresInDays(14);
      await loadAnnouncements(token);
    } catch (error) {
      setFormError(error.message);
    }
  };

  const handleDelete = async (announcement) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/announcements/${announcement.id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token.access_token}` },
        }
      );
      if (!response.ok) {
        throw new Error("Could not delete announcement");
      }
      await loadAnnouncements(token);
    } catch (error) {
      setFormError(error.message);
    }
  };

  const isExpired = (announcement) =>
    new Date(announcement.expires_at) <= new Date();

  if (!token) {
    return null;
  }

  return (
    <div className="text-white">
      <Nav />
      <div className="container mx-auto p-4 max-w-2xl">
        <h1 className="text-2xl font-bold mb-4">Manage Announcements</h1>

        <form onSubmit={handleSubmit} className="mb-6">
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
            rows={2}
            placeholder="e.g. Spanish language support added! Check My Profile to switch."
            className="w-full p-2 border rounded text-black"
          />
          <div className="mt-2 flex items-center gap-2">
            <label>Expires in (days):</label>
            <input
              type="number"
              min="0"
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(e.target.value)}
              className="w-20 p-1 border rounded text-black"
            />
            <button type="submit" className="p-1 border rounded">
              Add Announcement
            </button>
          </div>
        </form>

        {formError && <p className="text-red-500 mb-4">{formError}</p>}

        <table className="min-w-full divide-y divide-gray-200 text-left">
          <thead>
            <tr>
              <th className="pr-4">Message</th>
              <th className="pr-4">Created</th>
              <th className="pr-4">Expires</th>
              <th className="pr-4">Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {announcements.map((announcement) => (
              <tr key={announcement.id}>
                <td className="pr-4 py-1 max-w-sm">{announcement.message}</td>
                <td className="pr-4 py-1">
                  {new Date(announcement.created_at).toLocaleString()}
                </td>
                <td className="pr-4 py-1">
                  {new Date(announcement.expires_at).toLocaleString()}
                </td>
                <td className="pr-4 py-1">
                  {isExpired(announcement) ? "Expired" : "Active"}
                </td>
                <td className="py-1">
                  <button
                    onClick={() => handleDelete(announcement)}
                    className="p-1 border rounded"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
